import config from '@config';
import { brandsOpenApi } from '@constants';
import { axiosBoa, clarityUtil, errorHandler } from '@utils';
import { getParent, types } from 'mobx-state-tree';
import { models } from './constants';
import { createStoreAction } from './utils';

export const AuthStore = types
    .model('AuthStore', {
        pending: true,
        oauthPayload: types.maybeNull(types.frozen()),
        embeddedLoadPayload: types.maybeNull(types.frozen()),
        // shows if this bc account has users when installing in oauth process
        hasUser: types.maybeNull(types.boolean),
        hasInviteDeveloper: types.maybeNull(types.boolean),
        isFormPending: types.maybeNull(types.boolean),
        me: types.optional(
            types.union(
                types.model('MeModel', {
                    id: types.maybeNull(types.number),
                    firstName: types.maybeNull(types.string),
                    lastName: types.maybeNull(types.string),
                    passwordExist: types.maybeNull(types.boolean),
                    phone: types.maybeNull(types.string),
                    email: types.maybeNull(types.string),
                    currentAccount: types.maybeNull(models.AccountModel),
                    accounts: types.array(
                        types.model({
                            account: models.AccountModel
                        })
                    ),
                    permission: types.maybeNull(
                        types.model({
                            role: types.maybe(types.number),
                            status: types.maybe(types.number)
                        })
                    )
                }),
                types.literal(undefined),
                types.literal(null)
            ),
            undefined
        )
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        get userId() {
            return self?.me?.id;
        },
        get currentAccountId() {
            return self.me?.currentAccount?.id;
        },
        get firstName() {
            return self.me?.firstName || '';
        },
        get lastName() {
            return self.me?.lastName || '';
        },
        get companyName() {
            const companyName = self.me?.currentAccount?.companyName;
            const storeName = self.me?.currentAccount?.storeName;
            return companyName || storeName || '';
        },
        get accounts() {
            return self.me?.accounts?.map((item) => item.account) || [];
        },
        get hasAccount() {
            return !!self.me?.accounts?.length;
        },
        get logoUrl() {
            return self.me?.currentAccount?.logoUrl || null;
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            setInviteDeveloper: (value) => {
                self.hasInviteDeveloper = value;
            },
            setEmbeddedLoadPayload: (payload) => {
                self.embeddedLoadPayload = payload;
            },
            setOauthPayload: (link) => {
                self.oauthPayload = link;
            },
            setPixelMeta: (data) => {
                if (window.klpixel) {
                    const userId = data?.id ?? '';
                    const accountId = data?.currentAccount?.id ?? '';
                    const shopDomain = data?.currentAccount?.shopDomain ?? '';

                    window.klpixel('meta', 'dashboard', {
                        userId: String(userId),
                        accountId: String(accountId),
                        shopDomain,
                        version: 'v2'
                    });
                }
            },
            setClarityMeta: () => {
                clarityUtil.setTag({
                    key: 'account_id',
                    value: self.currentAccountId
                });

                clarityUtil.identify({
                    customerId: self.userId,
                    friendlyName: `${self.firstName} ${self.lastName}`
                });
            },
            signIn: action(function* signIn(values) {
                const response = yield axiosBoa.post(brandsOpenApi.auth.signIn, values);
                yield self.whoAmI();
                return response;
            }),
            platformOauthRedirect: action(function* platformOauthRedirect(platform, method) {
                const redirect = yield axiosBoa.get(brandsOpenApi.oauth.redirect(platform, method));
                window.location.replace(redirect.url);
            }),
            platformOauthCallback: action(function* platformOauthCallback(platform, params) {
                const response = yield axiosBoa.get(brandsOpenApi.oauth.callback(platform), { params });
                return response;
            }),
            logout: action(function* logout() {
                yield axiosBoa.get(brandsOpenApi.auth.logout);
                self.me = null;
            }),
            signUp: action(
                function* signUp(values) {
                    self.isFormPending = true;

                    const response = yield axiosBoa.post(brandsOpenApi.auth.signUp, values);
                    yield self.whoAmI();
                    return response;
                },
                { onFinally: () => (self.isFormPending = false) }
            ),
            whoAmI: action(
                function* whoAmI() {
                    const response = yield axiosBoa.get(brandsOpenApi.auth.me);
                    self.me = response;
                    self.setPixelMeta(response);
                    self.setClarityMeta(response);
                },
                { onFinally: () => (self.pending = false), onError: () => (self.me = null) }
            ),
            getInitialData: action(function* getInitialData() {
                yield self.whoAmI();
                if (self.me?.currentAccount) {
                    yield self.root.accountsStore.getSetupInfo();
                    yield self.root.accountsStore.getCurrentAccount();
                    yield self.root.googleAdsStore.getData();
                    if (self.root.accountsStore.isShopify) {
                        yield self.root.integrationsStore.checkScopes();
                    }
                }
            }),
            createCustomAccount: action(
                function* createCustomAccount(values) {
                    self.isFormPending = true;

                    yield axiosBoa.post(brandsOpenApi.auth.createCustomAccount, values);
                    yield self.getInitialData();
                    return true;
                },
                { onFinally: () => (self.isFormPending = false) }
            ),
            switchAccount: action(
                function* switchAccount(accountId) {
                    self.switchPending = true;

                    yield axiosBoa.post(brandsOpenApi.auth.switchAccount, { accountId });
                },
                { onFinally: () => (self.switchPending = false) }
            ),
            getEcommerceRedirect: action(
                function* getEcommerceRedirect(platform, params) {
                    self.isFormPending = true;
                    const redirect = yield axiosBoa.get(brandsOpenApi.auth.getEcommerceRedirect(platform), {
                        params
                    });
                    window.location.replace(redirect.url);

                    return true;
                },
                {
                    onFinally: () => (self.isFormPending = false),
                    onError: (error) => {
                        errorHandler({ error, duration: 60 });
                        return false;
                    }
                }
            ),
            onConnectUserToAccount: action(
                function* onConnectUserToAccount(setLoadingCallback) {
                    setLoadingCallback(true);
                    yield axiosBoa.get(brandsOpenApi.auth.userAccountConnect);
                    window.location.replace(config.connectorAppUrl + '/status');
                },
                { onFinally: (setLoadingCallback) => setLoadingCallback(false) }
            ),
            onUserCheckEmail: action(
                function* onUserCheckEmail(setLoading, email) {
                    setLoading(true);
                    const response = yield axiosBoa.post(brandsOpenApi.auth.userCheckEmail, { email });
                    return response;
                },
                { onFinally: (setLoading) => setLoading(false) }
            ),
            updateAccount: (accountId, data) => {
                const allowedKeys = ['id', 'companyName', 'storeName', 'status', 'logoUrl'];
                const filteredAccount = Object.keys(data)
                    .filter((key) => allowedKeys.includes(key))
                    .reduce((acc, key) => {
                        acc[key] = data[key];
                        return acc;
                    }, {});
                const accountToUser = self.me.accounts.find((a) => a.account.id === accountId);
                if (accountToUser) {
                    Object.assign(accountToUser.account, filteredAccount);
                }
                if (self.me.currentAccount?.id === accountId) {
                    Object.assign(self.me.currentAccount, filteredAccount);
                }
            },
            checkAccountUser: action(function* checkAccountUser(params) {
                const response = yield axiosBoa.get(brandsOpenApi.auth.checkAccountUser, {
                    params: {
                        ...params,
                        externalId: self.oauthPayload?.context?.split('/')[1] || null
                    }
                });
                self.hasUser = response.hasUser;
                return response.hasUser;
            })
        };
    });
